import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Dumbbell, Award, Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { DateInput } from '@/components/ui/DateInput';
import { useToast } from '@/shared/hooks/use-toast';
import { useI18n } from '@/contexts/I18nContext';
import { supabase } from '@/integrations/supabase/client';
import { normalizeUsername, validateUsername } from '@/utils/usernameValidation';
import { validatePassword, getPasswordStrength } from '@/utils/passwordValidation';
import { validateDateOfBirth, todayYmd } from '@/utils/ageGate';
import { Button } from '@/components/ui/button';
import { GoogleAuthButton } from './GoogleAuthButton';
import { cn } from '@/lib/utils';

export interface SignUpFormData {
  email: string;
  isInstructor: boolean;
  username: string;
  password: string;
  confirmPassword: string;
  dateOfBirth: string;
}

interface SignUpFormProps {
  onSubmit: (data: SignUpFormData) => Promise<void>;
  loading: boolean;
}

type UsernameStatus = 'idle' | 'checking' | 'available' | 'unavailable' | 'invalid';

const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const trimEmail = (email: string) => email.trim();

export const SignUpForm = ({ onSubmit, loading }: SignUpFormProps) => {
  const { toast } = useToast();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const typeHint = searchParams.get('type');

  const [formData, setFormData] = useState<SignUpFormData>({
    email: '',
    isInstructor: typeHint === 'instrutor',
    username: '',
    password: '',
    confirmPassword: '',
    dateOfBirth: '',
  });

  const [errors, setErrors] = useState({ email: '', password: '', confirmPassword: '', dateOfBirth: '' });
  const [touched, setTouched] = useState({ email: false, password: false, confirmPassword: false, dateOfBirth: false });

  const [usernameStatus, setUsernameStatus] = useState<UsernameStatus>('idle');
  const [usernameTouched, setUsernameTouched] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.username && usernameTouched) {
        checkUsername(formData.username);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.username]);

  const checkUsername = async (value: string) => {
    const normalized = normalizeUsername(value);
    if (!validateUsername(normalized)) {
      setUsernameStatus('invalid');
      return;
    }
    setUsernameStatus('checking');
    const { data, error } = await supabase.rpc('check_username_available', { input_username: normalized });
    if (error) { setUsernameStatus('invalid'); return; }
    setUsernameStatus(data ? 'available' : 'unavailable');
  };

  const usernameValidationState = !usernameTouched
    ? undefined
    : usernameStatus === 'available'
      ? 'success' as const
      : usernameStatus === 'checking'
        ? 'checking' as const
        : usernameStatus === 'unavailable'
          ? 'unavailable' as const
          : usernameStatus === 'invalid'
            ? 'error' as const
            : undefined;

  const usernameMessage = !usernameTouched ? '' :
    usernameStatus === 'available' ? `@${formData.username} ${t('auth.step_two.username_available') || 'está disponível'}` :
    usernameStatus === 'unavailable' ? t('auth.step_two.username_unavailable') || 'Username indisponível' :
    usernameStatus === 'checking' ? t('auth.step_two.checking_username') || 'Verificando...' :
    usernameStatus === 'invalid' ? t('auth.step_two.username_invalid') || 'Username inválido' : '';

  const maxDob = useMemo(() => todayYmd(), []);

  const passwordStrength = getPasswordStrength(formData.password);
  const strengthLabel = !formData.password
    ? t('auth.step_two.password_strength.hint') || 'Use letras, números e símbolos.'
    : passwordStrength <= 1
      ? t('auth.step_two.password_strength.weak') || 'Senha fraca'
      : passwordStrength === 2
        ? t('auth.step_two.password_strength.fair') || 'Senha razoável'
        : passwordStrength === 3
          ? t('auth.step_two.password_strength.good') || 'Senha boa'
          : t('auth.step_two.password_strength.strong') || 'Senha forte';

  const isFormValid =
    validateEmail(trimEmail(formData.email)) &&
    validateDateOfBirth(formData.dateOfBirth) === null &&
    formData.username &&
    usernameStatus === 'available' &&
    validatePassword(formData.password) &&
    formData.password === formData.confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setTouched({ email: true, password: true, confirmPassword: true, dateOfBirth: true });
    setUsernameTouched(true);

    const email = trimEmail(formData.email);
    if (email !== formData.email) {
      setFormData(prev => ({ ...prev, email }));
    }

    if (!validateEmail(email)) {
      setErrors(prev => ({ ...prev, email: t('auth.step_one.email_invalid') || 'E-mail inválido' }));
      return;
    }

    if (validateDateOfBirth(formData.dateOfBirth) !== null) {
      setErrors(prev => ({ ...prev, dateOfBirth: t('auth.step_two.birthday_required') || 'Informe sua data de nascimento.' }));
      return;
    }

    // Check email existence
    try {
      const { data } = await supabase.rpc('check_email_exists', { email_to_check: email });
      if (data === true) {
        toast({
          title: t('auth.step_one.email_already_registered') || 'E-mail já cadastrado',
          description: t('auth.step_one.email_already_registered_description') || 'Tente fazer login.',
          variant: 'destructive',
        });
        return;
      }
    } catch {
      toast({ title: t('auth.error') || 'Erro', description: t('auth.step_one.could_not_check_email') || 'Não foi possível verificar o e-mail.', variant: 'destructive' });
      return;
    }

    if (!formData.username || usernameStatus !== 'available') {
      toast({ title: t('auth.error') || 'Erro', description: t('auth.step_two.username_invalid') || 'Escolha um username válido e disponível.', variant: 'destructive' });
      return;
    }

    if (!validatePassword(formData.password)) {
      setErrors(prev => ({ ...prev, password: t('auth.step_two.password_requirements.characters') || 'Senha muito fraca' }));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setErrors(prev => ({ ...prev, confirmPassword: t('auth.step_two.passwords_not_match') || 'As senhas não coincidem' }));
      return;
    }

    await onSubmit({ ...formData, email });
  };

  const handleEmailBlur = () => {
    setTouched(prev => ({ ...prev, email: true }));
    const email = trimEmail(formData.email);
    if (email !== formData.email) {
      setFormData(prev => ({ ...prev, email }));
    }
    if (email && !validateEmail(email)) {
      setErrors(prev => ({ ...prev, email: t('auth.step_one.email_invalid') || 'E-mail inválido' }));
    }
  };

  return (
    <div className="w-full flex flex-col min-h-[calc(100dvh-140px)]">
      <div className="flex-1 space-y-6 pb-4">
      {/* Prominent heading + tagline — this is the ads landing screen. */}
      <div className="mb-1">
        <h2 className="text-[28px] font-extrabold leading-tight tracking-tight text-white">
          {t('auth.step_one.title')}
        </h2>
        <p className="mt-1 text-[15px] text-white/60">
          {t('auth.step_two.signup_tagline')}
        </p>
      </div>

      {/* Social sign-up */}
      <GoogleAuthButton label={t('auth.google.signup')} />

      {/* Divider */}
      <div className="flex items-center gap-3">
        <span className="h-px flex-1 bg-white/10" />
        <span className="text-xs text-white/40">{t('auth.google.or')}</span>
        <span className="h-px flex-1 bg-white/10" />
      </div>

      <form id="signup-form" onSubmit={handleSubmit} noValidate className="space-y-4">
        {/* Email */}
        <div className="space-y-1">
          <label className="text-xs font-medium text-white/70">{t('auth.email') || 'E-mail'}</label>
          <Input
            type="email"
            autoComplete="email"
            placeholder="seu@email.com"
            value={formData.email}
            onChange={(e) => {
              setFormData(prev => ({ ...prev, email: e.target.value }));
              setErrors(prev => ({ ...prev, email: '' }));
            }}
            onBlur={handleEmailBlur}
            showFloatingLabel={false}
            error={!!(errors.email && touched.email)}
          />
          {errors.email && touched.email && <p className="text-xs text-red-300">{errors.email}</p>}
        </div>

        {/* Data de nascimento (age gate embutido) */}
        <div className="space-y-1">
          <label className="text-xs font-medium text-white/70" htmlFor="signup-dob">
            {t('auth.step_two.birthday_label') || 'Data de nascimento'}
          </label>
          <DateInput
            id="signup-dob"
            value={formData.dateOfBirth}
            onChange={(v) => {
              setFormData(prev => ({ ...prev, dateOfBirth: v }));
              setErrors(prev => ({ ...prev, dateOfBirth: '' }));
            }}
            onBlur={() => setTouched(prev => ({ ...prev, dateOfBirth: true }))}
            max={maxDob}
            ariaInvalid={!!(errors.dateOfBirth && touched.dateOfBirth)}
            ariaDescribedBy={errors.dateOfBirth && touched.dateOfBirth ? 'signup-dob-error' : undefined}
            dataSignupField="dateOfBirth"
            inputClassName="bg-sossiety-input text-white border-[rgba(255,255,255,0.1)] placeholder:text-white/40 focus:ring-sossiety-red/40 !rounded-xl"
          />
          {errors.dateOfBirth && touched.dateOfBirth && (
            <p id="signup-dob-error" className="text-xs text-red-300">{errors.dateOfBirth}</p>
          )}
        </div>

        {/* Tipo: Praticante / Instrutor */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-white/70">{t('auth.step_two.are_you') || 'Eu sou'}</label>
          <div className="grid grid-cols-2 gap-3">
            {/* Praticante */}
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, isInstructor: false }))}
              className={cn(
                'relative flex flex-col items-center gap-2 rounded-xl border px-4 py-4 transition-all',
                !formData.isInstructor
                  ? 'border-sossiety-red bg-sossiety-red-surface'
                  : 'border-[rgba(255,255,255,0.1)] bg-[#231a1a]'
              )}
            >
              <Dumbbell className="w-6 h-6 text-white" />
              <span className="text-sm font-medium text-white">{t('auth.step_two.practitioner') || 'Praticante'}</span>
              {!formData.isInstructor && (
                <span className="absolute top-2 right-2 w-5 h-5 bg-sossiety-red rounded-full flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </span>
              )}
            </button>

            {/* Instrutor */}
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, isInstructor: true }))}
              className={cn(
                'relative flex flex-col items-center gap-2 rounded-xl border px-4 py-4 transition-all',
                formData.isInstructor
                  ? 'border-sossiety-red bg-sossiety-red-surface'
                  : 'border-[rgba(255,255,255,0.1)] bg-[#231a1a]'
              )}
            >
              <Award className="w-6 h-6 text-white" />
              <span className="text-sm font-medium text-white">{t('auth.step_two.instructor') || 'Instrutor'}</span>
              {formData.isInstructor && (
                <span className="absolute top-2 right-2 w-5 h-5 bg-sossiety-red rounded-full flex items-center justify-center">
                  <Check className="w-3 h-3 text-white" />
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Username */}
        <div className="space-y-1">
          <label className="text-xs font-medium text-white/70">{t('auth.step_two.choose_username') || 'Username'}</label>
          <Input
            type="text"
            autoComplete="off"
            placeholder="@seuusername"
            value={formData.username}
            onChange={(e) => {
              const normalized = normalizeUsername(e.target.value);
              setFormData(prev => ({ ...prev, username: normalized }));
              setUsernameTouched(true);
              setUsernameStatus('idle');
            }}
            onBlur={() => {
              setUsernameTouched(true);
              if (formData.username) checkUsername(formData.username);
            }}
            maxLength={32}
            showFloatingLabel={false}
            validationState={usernameValidationState}
            validationMessage={usernameTouched ? usernameMessage : undefined}
          />
        </div>

        {/* Password */}
        <div className="space-y-1">
          <label className="text-xs font-medium text-white/70">{t('auth.step_two.password_label') || 'Senha'}</label>
          <PasswordInput
            autoComplete="new-password"
            placeholder={t('auth.step_two.password_placeholder') || 'Crie uma senha forte'}
            value={formData.password}
            onChange={(e) => {
              setFormData(prev => ({ ...prev, password: e.target.value }));
              setErrors(prev => ({ ...prev, password: '' }));
            }}
            onBlur={() => setTouched(prev => ({ ...prev, password: true }))}
            error={!!(errors.password && touched.password)}
            showFloatingLabel={false}
          />
          {/* Medidor de força de senha */}
          <div className="flex gap-1.5 mt-2" aria-hidden="true">
            {[0, 1, 2, 3].map((i) => (
              <span
                key={i}
                className={cn(
                  'h-1 flex-1 rounded-full transition-colors',
                  i < passwordStrength
                    ? passwordStrength <= 1
                      ? 'bg-sossiety-red'
                      : passwordStrength === 2
                        ? 'bg-sossiety-alert'
                        : passwordStrength === 3
                          ? 'bg-yellow-400'
                          : 'bg-sossiety-success'
                    : 'bg-white/10'
                )}
              />
            ))}
          </div>
          <p className="text-xs text-white/40">{strengthLabel}</p>
          {errors.password && touched.password && <p className="text-xs text-red-300">{errors.password}</p>}
        </div>

        {/* Confirm Password */}
        <div className="space-y-1">
          <label className="text-xs font-medium text-white/70">{t('auth.step_two.confirm_password_label') || 'Confirmar senha'}</label>
          <PasswordInput
            autoComplete="new-password"
            placeholder={t('auth.step_two.confirm_password_placeholder') || 'Repita a senha'}
            value={formData.confirmPassword}
            onChange={(e) => {
              setFormData(prev => ({ ...prev, confirmPassword: e.target.value }));
              setErrors(prev => ({ ...prev, confirmPassword: '' }));
            }}
            onBlur={() => setTouched(prev => ({ ...prev, confirmPassword: true }))}
            error={!!(formData.confirmPassword && formData.password !== formData.confirmPassword)}
            showFloatingLabel={false}
          />
          {formData.confirmPassword && formData.password !== formData.confirmPassword && (
            <p className="text-xs text-red-300">{t('auth.step_two.passwords_not_match') || 'As senhas não coincidem'}</p>
          )}
        </div>

      </form>
      </div>

      <div className="sticky bottom-0 bg-sossiety-dark pt-3 pb-2 space-y-3">
        <p className="text-xs text-white/40 text-center leading-relaxed">
          {t('auth.step_two.consent_text') || 'Ao criar conta, você concorda com nossos'}{' '}
          <a href="/terms-of-use" target="_blank" rel="noopener noreferrer" className="text-sossiety-red hover:underline">
            {t('auth.step_two.terms_of_use') || 'Termos de Uso'}
          </a>
          {' '}{t('auth.step_two.and') || 'e'}{' '}
          <a href="/privacy-policies" target="_blank" rel="noopener noreferrer" className="text-sossiety-red hover:underline">
            {t('auth.step_two.privacy_policies') || 'Política de Privacidade'}
          </a>.
        </p>
        <Button
          form="signup-form"
          type="submit"
          variant="primary"
          size="lg"
          disabled={loading || !isFormValid}
          className="w-full"
        >
          {loading
            ? (t('auth.step_two.creating_account') || 'Criando conta...')
            : (t('auth.step_two.create_account') || 'Criar conta')}
        </Button>
      </div>
    </div>
  );
};
